import React, {
  useCallback,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from "react"
import IconButton from "@mui/material/IconButton"
import { IoApps, IoBookOutline } from "react-icons/io5"
import { TfiHelpAlt } from "react-icons/tfi"
// import { CgFileDocument } from "react-icons/cg"
import { styles } from "./ActivityTools.styles"
import { Menu, Typography, Stack, Box, useTheme, useMediaQuery } from "@mui/material"
import Image from "components/Image"
import { useTranslation } from "react-i18next"
import SomethingWrongModal from "components/Tools/SomethingWrongModal"
import html2canvas from "html2canvas"
import useApi from "modules/technicalSupport"
import { useAuthContext } from "contexts/AuthContext"
import { useModuleContext } from "contexts/ModuleContext"
import { useActiveContentContext } from "contexts/ActiveContentContext"
import TranslatorModal from "components/Tools/TranslatorModal"
import LibraryModal from "components/Tools/LibraryModal"
import NotesModal from "components/Tools/NotesModal"
import SubmitSuccess from "components/Tools/SubmitSucces"
import { ACTIVITY_VIEW_MODES } from "pages/student/activity/common/constants"
import useTour from "hooks/useTour"
import LightTooltip from "components/UI/Tooltip"
import TeacherMessage from "components/Tools/TeacherMessage"
import { maxWidth600 } from "themes/default/breakpoints"

export const ActivityTools = forwardRef(
  (
    {
      activityContent,
      extraTools = [],
      fontSettings = {
        fontSize: undefined,
        fontFamily: undefined,
        bgColor: undefined,
      },
      hideTools = false,
      sx = {},
      showLabel = false,
    },
    parentRef
  ) => {
    const { t } = useTranslation()
    const { selectedGroupData, updateUserData, userData } = useAuthContext()
    const { viewMode, activity, studentAnswers } = useModuleContext()
    const { activeContent } = useActiveContentContext()
    const [anchorElTools, setAnchorElTools] = React.useState(null)
    const [openSomethingWrong, setOpenSomethingWrong] = React.useState(false)
    const [openTranslate, setOpenTranslate] = React.useState(false)
    const [openLibrary, setOpenLibrary] = React.useState(false)
    const [openNotes, setOpenNotes] = React.useState(false)
    const [openTeacherMessages, setOpenTeacherMessages] = React.useState(false)
    const [screenshot, setScreenshot] = React.useState(null)
    const { getSupportSections, addTechnicalSupport } = useApi()
    const [submitSuccess, setSubmitSuccess] = React.useState(false)
    const [loadingScreenshot, setLoadingScreenshot] = React.useState(false)
    const { startTour } = useTour()
    const [currentErrorSequence, setCurrentErrorSequence] = React.useState(0)
    const buttonRef = React.useRef(null)
    const [continueTour, setContiueTour] = React.useState(false)
    const [shouldTakeScreenshot, setShouldTakeScreenshot] =
      React.useState(false)
    const theme = useTheme()
    const [highlightDarkMode, setHighlightDarkMode] = React.useState(false)
    const matches = useMediaQuery(maxWidth600)

    useImperativeHandle(
      parentRef,
      () => ({
        openTools: (highlightDarkModeButton = false) => {
          if (highlightDarkModeButton) {
            setHighlightDarkMode(true)
            setTimeout(() => {
              setHighlightDarkMode(false)
            }, 3000) // Resaltar por 3 segundos
          }
          buttonRef?.current?.click()
        },
        closeTools: () => {
          setAnchorElTools(null)
        },
        openNotes: (isOpen) => {
          setOpenNotes(isOpen)
        },
        openLibrary: (isOpen) => {
          setContiueTour(isOpen)
          setOpenLibrary(isOpen)
        },
      }),
      []
    )

    useEffect(() => {
      window.openHelpTour = startTourHelpForActivity
      return () => {
        window.openHelpTour = undefined
      }
    }, [activity, studentAnswers])

    const handleOpenTools = (event) => {
      setAnchorElTools(event.currentTarget)
    }

    const handleCloseTools = (item) => {
      // console.log("🚀 ~ file: index.js:72 ~ handleCloseTools ~ item:", item)
      if (item?.onClick) {
        item.onClick()
      }
      setAnchorElTools(null)
    }

    const takeScreenshot = async () => {
      try {
        setLoadingScreenshot(true)
        const canvas = await html2canvas(document.body, {
          allowTaint: true,
          useCORS: true,
          width: window.innerWidth,
          height: window.innerHeight,
          scrollX: 0,
          scrollY: -window.scrollY,
        })
        const image = canvas.toDataURL()
        //  console.log("🚀 ~ takeScreenshot ~ image:", image)
        setScreenshot(image)
      } catch (error) {
        console.warn("Error taking screenshot:", error)
      } finally {
        setLoadingScreenshot(false)
      }
    }

    const onContentError = useCallback(() => {
      // take screenshot of current page using html2canvas
      setCurrentErrorSequence(1)
      takeScreenshot()
      setOpenSomethingWrong(true)
    }, [activityContent])

    const onGlitchInTheSystem = useCallback(() => {
      // take screenshot of current page using html2canvas
      setCurrentErrorSequence(2)
      takeScreenshot()
      setOpenSomethingWrong(true)
    }, [activityContent])

    const onTeacherMessage = useCallback(() => {
      // take screenshot of current page using html2canvas
      setShouldTakeScreenshot(true)
      setOpenTeacherMessages(true)
    }, [])

    const onTeacherMessageClosed = useCallback(() => {
      setShouldTakeScreenshot(false)
      setOpenTeacherMessages(false)
    }, [])

    const onTechnicalSupportSubmit = useCallback(
      async (data) => {
        const response = await getSupportSections()
        if (response) {
          const section = response.find(
            (section) => section.sequence === currentErrorSequence
          )?.sections
          if (section && section.length > 0) {
            const supportTypeId = section[0].supportTypeId
            const screenshotFile = await fetch(screenshot)
            const imageBlob = await screenshotFile.blob()
            const imageFile = new File([imageBlob], "screenshot.png", {
              type: "image/png",
            })
            let attachFile = null
            if (
              data?.attachedImage !== undefined &&
              data.attachedImage !== null
            ) {
              const attachedImageFile = await fetch(data?.attachedImage)
              const attachedImageBlob = await attachedImageFile.blob()
              attachFile = new File([attachedImageBlob], "attachedImage.png", {
                type: "image/png",
              })
            }
            let relatedId = activeContent.relatedId;
            let subRelatedId = activeContent.subRelatedId;
            if (activeContent?.screen !== "content" && activeContent?.screen !== "games") {
              relatedId = selectedGroupData.groupId;
              subRelatedId = 0;
            }

            await addTechnicalSupport({
              supportTypeId,
              screen: activeContent?.screen,
              comment: data?.description,
              imageFile: attachFile,
              relatedId,
              subRelatedId,
              screenShot: imageFile,
            })
            setSubmitSuccess(true)
          } else {
            console.log("no sections")
          }
        } else {
          console.log("no response")
        }
        setOpenSomethingWrong(false)
      },
      [screenshot, currentErrorSequence, activeContent]
    )

    const startTourHelpForActivity = useCallback(() => {
      // if (studentAnswers !== null) return
      const currentActivity = activity
      if (!currentActivity?.activityTypeId) return
      startTour({
        activityTypeId: currentActivity.activityTypeId,
      })
    }, [activity, studentAnswers])

    const onDarkModeSwitch = useCallback(() => {
      const isDarkMode = theme.palette.mode === "dark"
      updateUserData({
        newUserData: {
          uiSettings: {
            theme: isDarkMode ? "Light" : "Dark",
          },
        },
      })
      localStorage.setItem("smash-prefers-dark-mode", (!isDarkMode).toString())
      if (window.applyDarkMode) {
        window.applyDarkMode()
      }
    }, [theme])

    const tools = [
      ...(viewMode === ACTIVITY_VIEW_MODES.STUDENT
        ? [
          {
            name: t("your_notes"),
            icon: <Image name="notesTool" width={32} height={32} />,
            onClick: () => {
              setOpenNotes(true)
            },
            order: 4,
            className: "notes-tool-btn",
          },
          {
            name: t("your_library"),
            icon: <IoBookOutline style={styles.iconMenu(theme)} />,
            onClick: () => {
              setOpenLibrary(true)
            },
            order: 5,
            className: "library-tool-btn",
          },
        ]
        : []),
      ...(viewMode === ACTIVITY_VIEW_MODES.STUDENT &&
        selectedGroupData?.teachers?.some((teacher) => teacher.allowGradeReview)
        ? [
          {
            name: t("teacher_messages"),
            icon: <Image name="messages" width={32} height={32} />,
            onClick: onTeacherMessage,
            order: 6,
          },
        ]
        : []),
      {
        name: t("content_error"),
        icon: <Image name="content" width={32} height={32} />,
        onClick: onContentError,
        order: 8,
      },
      {
        name: t("glitch_in_the_system"),
        icon: <Image name="glitch" width={32} height={32} />,
        onClick: onGlitchInTheSystem,
        order: 9,
      },
      ...(userData?.uiSettings?.theme?.toLowerCase() === "auto"
        ? []
        : [
          {
            id: "dark-mode",
            name:
              userData?.uiSettings?.theme?.toLowerCase() === "dark"
                ? t("light_mode")
                : t("dark_mode"),
            icon: (
              <Image
                name={
                  userData?.uiSettings?.theme?.toLowerCase() === "dark"
                    ? "selectLightMode"
                    : "selectDarkMode"
                }
                width={32}
                height={32}
              />
            ),
            onClick: onDarkModeSwitch,
            order: 10,
          },
        ]),
      ...(studentAnswers === null &&
        viewMode !== ACTIVITY_VIEW_MODES.TEACHER_EXERCISE_GRADE &&
        !hideTools
        ? [
          {
            name: t("faqs_and_tutorials"),
            icon: <TfiHelpAlt style={styles.iconMenu(theme)} />,
            onClick: startTourHelpForActivity,
            order: 7,
          },
        ]
        : []),
      ...(viewMode === ACTIVITY_VIEW_MODES.TEACHER_EXERCISE_GRADE || hideTools
        ? []
        : [
          {
            name: t("translator"),
            icon: <Image name="language" width={32} height={32} />,
            onClick: () => {
              setOpenTranslate(true)
            },
            order: 2,
          },
        ]),
      ...extraTools,
    ]

    return (
      <Box sx={[styles.toolsMenu, sx?.toolsMenu]}>
        <LightTooltip title={t("open_tools")} arrow>
          <IconButton
            sx={[styles.menuButton, sx?.menuButton]}
            onClick={handleOpenTools}
            className="tools-menu menu-tools-paper"
            ref={buttonRef}
          >
            <Stack alignItems={"center"} justifyContent={"center"}>
              <IoApps
                style={{
                  ...styles.menuIcon(theme),
                  ...sx?.menuIcon,
                }}
              />
              {!matches && showLabel && <Typography sx={styles.toolsText}>{t("tools")}</Typography>}
            </Stack>
          </IconButton>
        </LightTooltip>
        <Menu
          id="activity-tools-menu"
          anchorEl={anchorElTools}
          open={Boolean(anchorElTools)}
          onClose={handleCloseTools}
          PaperProps={styles.menuPaper}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          sx={{
            "& .MuiPaper-root": {
              backgroundColor:
                fontSettings?.bgColor || theme.palette.background.default,
              "&:before": {
                backgroundColor:
                  fontSettings?.bgColor || theme.palette.background.default,
              },
            },
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            // spacing={2}
            justifyContent={"flex-start"}
            flexWrap={"wrap"}
            sx={[
              {
                padding: ".5rem",
                maxWidth: 375,
              },
              fontSettings?.bgColor && {
                backgroundColor: fontSettings?.bgColor,
              },
            ]}
          >
            {tools
              .sort((a, b) => a.order - b.order)
              .map((tool) => (
                <IconButton
                  onClick={() => handleCloseTools(tool)}
                  key={tool.name}
                  sx={[
                    {
                      display: "flex",
                      flexDirection: "column",
                      // alignItems: "center",
                      justifyContent: "flex-start",
                      width: "33%",
                      borderRadius: 1.25,
                      // py: 1.25,
                      px: "1.5rem",
                      mb: ".5rem",
                    },
                    // Resaltar el botón de dark mode
                    highlightDarkMode &&
                    tool.id === "dark-mode" &&
                    styles.highlightButton,
                  ]}
                  className={tool?.className || ""}
                >
                  {tool.icon}
                  <Typography
                    textAlign="center"
                    variant="p"
                    sx={[
                      {
                        fontSize: 12,
                        color: theme.palette.common.black,
                        whiteSpace: "pre-wrap",
                        wordWrap: "break-word",
                      },
                      fontSettings?.fontFamily && {
                        fontFamily: fontSettings?.fontFamily,
                      },
                    ]}
                  >
                    {tool.name}
                  </Typography>
                </IconButton>
              ))}
          </Stack>
        </Menu>
        <SomethingWrongModal
          open={openSomethingWrong}
          onClose={() => setOpenSomethingWrong(false)}
          onSubmit={onTechnicalSupportSubmit}
          loadingScreenshot={loadingScreenshot}
          sequence={currentErrorSequence}
        >
          <img width={1000} alt={"Screenshot"} src={screenshot} />
        </SomethingWrongModal>
        <TranslatorModal
          open={openTranslate}
          onClose={() => setOpenTranslate(false)}
        />
        <LibraryModal
          open={openLibrary}
          onClose={() => {
            setOpenLibrary(false)
            setContiueTour(false)
          }}
          withTour={continueTour}
          fontSettings={fontSettings}
        />
        <NotesModal
          open={openNotes}
          fontSettings={fontSettings}
          onClose={() => setOpenNotes(false)}
        />
        <TeacherMessage
          open={openTeacherMessages}
          onClose={onTeacherMessageClosed}
          takeScreenshot={shouldTakeScreenshot}
        />
        <SubmitSuccess
          open={submitSuccess}
          onClose={() => setSubmitSuccess(false)}
        />
      </Box>
    )
  }
)

export default ActivityTools
